import type { McpCapability, McpToolAnnotations, RiskClass } from '@ralph/contracts'
import clsx from 'clsx'
import { AlertTriangle, Ban, Quote, Sparkles } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Notice, riseDelay, SectionHead, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Select, Spinner } from '@/components/ui/primitives.tsx'
import { useApplyCapabilities, useCapabilities, useDiscoverSystem, useUpdateCapability } from '@/hooks/use-systems.ts'
import { describeApiError, useI18n, useT } from '@/i18n/index.ts'
import { type BulkCounts, countBulk } from './bulk-enable.ts'
import { RISK_ORDER } from './system-limits.ts'

/**
 * Deciding what a connected system may actually do.
 *
 * This screen exists because the safe default produced an unsafe outcome. Every discovered
 * capability arrives switched off and classed as needing approval — which is right — and an
 * administrator facing forty of them has to judge each one from a name and a sentence
 * somebody else wrote. Forty judgements is a job nobody finishes, and what nobody finishes
 * stays off. The catalogue was safe and empty, and an empty catalogue is a product nobody
 * uses.
 *
 * So the platform brings evidence and an opinion, and is careful about the difference:
 *
 *  - **What the system says is drawn as a quotation.** Indented behind a rule, with a quote
 *    mark and an attribution — the visual grammar of somebody else's words, the same one the
 *    curation panel uses. Two panels of identical text would let the eye read a claim as a
 *    verified fact.
 *  - **What the platform proposes is drawn as a proposal**, with a sentence naming the claim
 *    it rests on and a control that has to be pressed. It is never pre-filled into the
 *    class selector, because a filled-in field is a decision somebody made.
 *  - **The bulk action selects nothing by default** and says, before it runs, how many of
 *    the chosen things change something in the world and how many of those do it without
 *    anybody being asked. Those are two questions; see `bulk-enable.ts` for what happened
 *    when one predicate answered both.
 */

export const CapabilityReview = ({ systemId, systemName }: { systemId: string; systemName: string }) => {
  const fmt = useI18n()
  const t = fmt.t.admin.systems
  const capabilities = useCapabilities(systemId)
  const discover = useDiscoverSystem(systemId)
  const apply = useApplyCapabilities(systemId)

  const [selected, setSelected] = useState<Set<string>>(new Set())

  const items = useMemo(
    () => (capabilities.data?.items ?? []).filter((item) => item.removedAt === null),
    [capabilities.data]
  )

  const selectable = useMemo(
    () => items.filter((item) => !item.enabled && item.quarantinedAt === null && item.rejectedReason === null),
    [items]
  )

  /**
   * The class each selected capability would be switched on at.
   *
   * A capability the platform has an opinion about uses that opinion; one it does not keeps
   * what it already holds, which is `privileged`. So a bulk action can only ever apply
   * suggestions the reader can see beside each row — it never invents a class for a row that
   * has no proposal attached.
   */
  const pending = useMemo(
    () =>
      selectable
        .filter((item) => selected.has(item.id))
        .map((item) => ({ capabilityId: item.id, risk: item.suggestion?.risk ?? item.risk })),
    [selectable, selected]
  )

  const counts = useMemo(() => countBulk(pending), [pending])

  const toggle = (id: string, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const selectReadable = () =>
    setSelected(new Set(selectable.filter((item) => item.suggestion?.risk === 'read').map((item) => item.id)))

  const failures = (apply.data ?? []).filter((entry) => !entry.ok)

  return (
    <section>
      <SectionHead
        title={t.review.title}
        lead={t.review.lead}
        action={
          <Button size="sm" disabled={discover.isPending} onClick={() => discover.mutate()}>
            {discover.isPending ? t.review.discovering : t.review.discover}
          </Button>
        }
      />

      {discover.isError && (
        <p role="alert" className="mt-3 text-[12.5px] text-alert">
          {describeApiError(fmt.t, discover.error)}
        </p>
      )}

      {capabilities.isLoading && (
        <p className="mt-3 flex items-center gap-2 text-[13px] text-fg-subtle">
          <Spinner /> {t.loading}
        </p>
      )}

      {!capabilities.isLoading && items.length === 0 && (
        <div className="mt-3">
          <EmptyState title={t.review.empty} body={t.review.emptyBody} />
        </div>
      )}

      {selectable.length > 0 && (
        <BulkBar
          counts={counts}
          canSelectReadable={selectable.some((item) => item.suggestion?.risk === 'read')}
          pending={apply.isPending}
          onSelectReadable={selectReadable}
          onClear={() => setSelected(new Set())}
          onApply={() => {
            apply.mutate(pending, { onSuccess: () => setSelected(new Set()) })
          }}
        />
      )}

      {failures.length > 0 && (
        <div className="mt-3">
          <Notice title={t.review.applyFailed(fmt, failures.length)} role="alert">
            <ul className="mt-1 space-y-0.5">
              {failures.map((entry) => (
                <li key={entry.capabilityId} className="readout">
                  {items.find((item) => item.id === entry.capabilityId)?.remoteName ?? entry.capabilityId} —{' '}
                  {describeApiError(fmt.t, entry.error)}
                </li>
              ))}
            </ul>
          </Notice>
        </div>
      )}

      <ul className="mt-4 space-y-2.5">
        {items.map((item, index) => (
          <li key={item.id}>
            <CapabilityCard
              item={item}
              index={index}
              systemId={systemId}
              systemName={systemName}
              selected={selected.has(item.id)}
              selectable={selectable.some((entry) => entry.id === item.id)}
              onSelect={(on) => toggle(item.id, on)}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * The bulk affordance, which is off until somebody turns it on.
 *
 * Nothing is pre-selected, and the one shortcut offered selects only what the system itself
 * describes as read-only — the single group where a mistake cannot change anything. The
 * confirmation counts writes and unattended writes separately, because "twelve things",
 * "twelve things, four of which change something" and "…and two of those with nobody
 * watching" are three different decisions.
 */
const BulkBar = ({
  counts,
  canSelectReadable,
  pending,
  onSelectReadable,
  onClear,
  onApply
}: {
  counts: BulkCounts
  canSelectReadable: boolean
  pending: boolean
  onSelectReadable: () => void
  onClear: () => void
  onApply: () => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.admin.systems

  return (
    <div className="mt-3.5 flex flex-wrap items-center gap-2 rounded-tile border border-rule bg-fg/[0.02] px-3 py-2.5">
      {canSelectReadable && (
        <Button size="sm" onClick={onSelectReadable}>
          {t.review.selectAllReadable}
        </Button>
      )}
      {counts.total > 0 && (
        <>
          <span className="readout text-fg-subtle">{t.review.selected(fmt, counts.total)}</span>
          <Button size="sm" onClick={onClear}>
            {t.review.clearSelection}
          </Button>
          <Button size="sm" variant="primary" disabled={pending} onClick={onApply}>
            {pending ? t.review.applying : t.review.apply}
          </Button>
          <p className="w-full text-[12.5px] leading-relaxed text-fg-muted">{t.review.applyBody(fmt, counts)}</p>
        </>
      )}
    </div>
  )
}

const CapabilityCard = ({
  item,
  index,
  systemId,
  systemName,
  selected,
  selectable,
  onSelect
}: {
  item: McpCapability
  index: number
  systemId: string
  systemName: string
  selected: boolean
  selectable: boolean
  onSelect: (on: boolean) => void
}) => {
  const messages = useT()
  const t = messages.admin.systems
  const update = useUpdateCapability(systemId)
  const held = item.quarantinedAt !== null
  // A row held because the platform now records something it did not before is a different
  // event from a row held because the system changed under us, and reading them as one is
  // how a review queue teaches people that being held back means nothing.
  //
  // Read from the cause the discovery recorded, never from the sentence it wrote beside it
  // (PRD-27 D-6). This branch used to match the first six words of that sentence, which made
  // the distinction a hostage to two things that move independently: an editor improving the
  // English, and this screen being read in Portuguese. Either one and every held row silently
  // becomes the other event. A cause this build cannot read, or one an older pod wrote during
  // a deploy, falls to the quarantine wording -- over-warning about a re-review is recoverable,
  // under-warning about a system that changed a judged tool is the thing the hold exists for.
  const reReview = held && item.quarantineCause === 're_review'

  return (
    <StudioRow style={riseDelay(index)}>
      <div className="flex flex-wrap items-start gap-x-2.5 gap-y-1.5">
        {selectable && (
          <input
            type="checkbox"
            checked={selected}
            className="mt-[5px] h-3.5 w-3.5 shrink-0 accent-primary"
            aria-label={t.review.selectLabel(item.title || item.remoteName)}
            onChange={(event) => onSelect(event.target.checked)}
          />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px]">
            {item.title || item.remoteName}
            <span className="readout ml-1.5 text-fg-subtle">{item.remoteName}</span>
          </h3>
        </div>
        <Badge tone={item.enabled ? 'grounded' : 'primary'}>
          {item.enabled ? t.review.enabled : t.review.notEnabled}
        </Badge>
      </div>

      {item.rejectedReason ? (
        <div className="mt-3">
          <Notice title={t.review.refusedTitle} icon={<Ban size={13} strokeWidth={2.1} />}>
            {item.rejectedReason}
          </Notice>
        </div>
      ) : (
        <>
          <Claim item={item} systemName={systemName} />

          {held && (
            <div className="mt-3">
              <Notice
                title={reReview ? t.review.reReviewTitle : t.review.quarantinedTitle}
                icon={<AlertTriangle size={13} strokeWidth={2.1} />}
              >
                {item.quarantineReason}
                <div className="mt-2">
                  <Button
                    size="sm"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ capabilityId: item.id, patch: { clearQuarantine: true } })}
                  >
                    {t.review.clearQuarantine}
                  </Button>
                </div>
              </Notice>
            </div>
          )}

          {item.suggestion && !held && (
            <Suggestion
              risk={item.suggestion.risk}
              because={item.suggestion.because}
              pending={update.isPending}
              onAccept={() =>
                update.mutate({ capabilityId: item.id, patch: { risk: item.suggestion?.risk ?? item.risk } })
              }
            />
          )}

          <div className="mt-3.5 flex flex-wrap items-end gap-3">
            <div className="min-w-[15rem] flex-1">
              <label htmlFor={`class-${item.id}`} className="eyebrow mb-1 block">
                {t.review.classLabel}
              </label>
              <Select
                id={`class-${item.id}`}
                value={item.risk}
                disabled={update.isPending}
                onChange={(event) =>
                  update.mutate({ capabilityId: item.id, patch: { risk: event.target.value as RiskClass } })
                }
              >
                {RISK_ORDER.map((risk) => (
                  <option key={risk} value={risk}>
                    {t.riskLabel[risk]}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-[12.5px] leading-snug text-fg-subtle">{t.riskNote[item.risk]}</p>
            </div>

            <Button
              size="sm"
              variant={item.enabled ? undefined : 'primary'}
              disabled={update.isPending || (held && !item.enabled)}
              onClick={() => update.mutate({ capabilityId: item.id, patch: { enabled: !item.enabled } })}
            >
              {update.isPending ? t.review.saving : item.enabled ? t.review.disable : t.review.enable}
            </Button>
          </div>

          {update.isError && (
            <p role="alert" className="mt-2.5 text-[12.5px] text-alert">
              {describeApiError(messages, update.error)}
            </p>
          )}
        </>
      )}
    </StudioRow>
  )
}

/**
 * The system's own words, and its own claims about behaviour, as a quotation.
 *
 * The behaviour hints are rendered as sentences rather than as flags, because `openWorldHint`
 * means nothing to the person deciding and "reaches outside the system" means everything.
 * They sit *inside* the quotation for the same reason the description does: they are the
 * system talking about itself, and the specification is explicit that they are untrusted.
 */
const Claim = ({ item, systemName }: { item: McpCapability; systemName: string }) => {
  const t = useT().admin.systems

  return (
    <blockquote className="mt-3 rounded-r-tile border-l-2 border-rule-strong bg-fg/[0.03] py-2.5 pr-3 pl-3.5">
      <p className="eyebrow flex items-center gap-1.5">
        <Quote size={10} strokeWidth={2.6} aria-hidden />
        {t.review.claimedBy(systemName)}
      </p>
      <p className="mt-1.5 text-[13px] leading-snug">{item.title || '—'}</p>
      <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">{item.description || '—'}</p>

      <p className="eyebrow mt-2.5">{t.review.behaviourTitle}</p>
      <BehaviourClaims annotations={item.annotations} />

      <p className="mt-1.5 text-[12px] leading-snug text-fg-subtle">{t.review.claimHint}</p>
    </blockquote>
  )
}

const BehaviourClaims = ({ annotations }: { annotations: McpToolAnnotations | null }) => {
  const t = useT().admin.systems
  if (!annotations) return <p className="mt-1 text-[12.5px] text-fg-muted">{t.review.behaviourNone}</p>

  // Only what the system actually said. An unstated hint is left out rather than rendered as
  // its default, because the defaults are the platform's reading of silence and putting them
  // in a quotation would attribute them to the system.
  const said: string[] = []
  if (annotations.readOnlyHint === true) said.push(t.review.hint.readOnly)
  if (annotations.readOnlyHint === false) said.push(t.review.hint.writes)
  if (annotations.destructiveHint === true) said.push(t.review.hint.destructive)
  if (annotations.destructiveHint === false) said.push(t.review.hint.notDestructive)
  if (annotations.idempotentHint === true) said.push(t.review.hint.idempotent)
  if (annotations.openWorldHint === true) said.push(t.review.hint.openWorld)
  if (annotations.openWorldHint === false) said.push(t.review.hint.closedWorld)

  if (said.length === 0) return <p className="mt-1 text-[12.5px] text-fg-muted">{t.review.behaviourNone}</p>

  return (
    <ul className="mt-1 space-y-0.5">
      {said.map((line) => (
        <li key={line} className="text-[12.5px] leading-snug text-fg-muted before:mr-1.5 before:content-['—']">
          {line}
        </li>
      ))}
    </ul>
  )
}

/**
 * A proposal with an author.
 *
 * Tinted with the primary wash and marked with an icon so it cannot be mistaken for either
 * the quotation above it or the control below it — it is neither the system's word nor a
 * decision, and a reader who confuses it with the second has been given a decision they did
 * not make. `because` names the claim it rests on, which is what makes accepting twelve of
 * these in one gesture a defensible act rather than a shrug.
 */
const Suggestion = ({
  risk,
  because,
  pending,
  onAccept
}: {
  risk: RiskClass
  because: string
  pending: boolean
  onAccept: () => void
}) => {
  const t = useT().admin.systems

  return (
    <div className={clsx('mt-3 rounded-tile border border-primary/30 bg-primary/[0.06] px-3 py-2.5')}>
      <p className="eyebrow flex items-center gap-1.5 text-primary">
        <Sparkles size={10} strokeWidth={2.6} aria-hidden />
        {t.review.suggestionTitle}
      </p>
      <p className="mt-1 text-[13px] leading-snug font-semibold">{t.riskLabel[risk]}</p>
      <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">{because}</p>
      <Button size="sm" className="mt-2" disabled={pending} onClick={onAccept}>
        {t.review.suggestionAccept}
      </Button>
    </div>
  )
}
